import { Card } from '@/components/ui/card';
import { Column, Table } from '@/system-logs/entities/system-logs';
import { cls } from '@/utils/cls';
import { formatDate, formatDatetime } from '@/utils/format/date';
import { formatNumber } from '@/utils/format/number';

export type SystemLogsTableProps = {
  table: Table;
};

/**
 * Format a duration in milliseconds as a human-readable string.
 */
function formatDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '';
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Render a cell value based on its column type and format.
 */
function renderCell(col: Column, cell: unknown): string {
  if (cell === null || cell === undefined) return '';

  // Normalize type as string for safety
  const type = String(col.type).toLowerCase();
  const format = col.format ? String(col.format).toLowerCase() : undefined;

  switch (type) {
    case 'string':
      return String(cell);
    case 'number': {
      const n = Number(cell);
      if (!Number.isFinite(n)) return '';
      if (format === 'integer') return formatNumber(n, 0, 0);
      if (format === 'duration') return formatDurationMs(n);
      return formatNumber(n, 2, 2);
    }
    case 'datetime': {
      const d = new Date(String(cell));
      if (isNaN(d.getTime())) return '';
      return formatDatetime(d);
    }
    case 'date': {
      const d = new Date(String(cell));
      if (isNaN(d.getTime())) return '';
      return formatDate(d);
    }
    default:
      // Fallback: best-effort stringification
      return String(cell);
  }
}

/**
 * Returns true if the column type is 'number' (case-insensitive).
 */
function isNumberColumn(col: Column): boolean {
  return String(col.type).toLowerCase() === 'number';
}

export function SystemLogsTable({ table }: SystemLogsTableProps) {
  const columns = table.columns;
  const rows = table.rows;

  return (
    <Card className="flex flex-1 flex-col overflow-hidden p-4">
      <div className="size-full overflow-auto">
        <table
          className="min-w-full text-left text-sm"
          data-testid="system-logs-table"
        >
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.id}
                  className={cls('sticky top-0 bg-white p-2 font-medium')}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="p-8 text-center text-gray-500"
                >
                  No logs found for the selected filters
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="odd:bg-gray-50 hover:bg-gray-200">
                  {columns.map((col) => (
                    <td
                      key={col.id}
                      className={cls(
                        'p-2 align-top',
                        isNumberColumn(col) && 'text-right',
                      )}
                    >
                      {renderCell(col, (row.cells as any)[col.id])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
